import { useMutation, useQueryClient } from 'react-query'
import { UseMutationOptions } from 'react-query/types/react/types'
import { BrandMediaFile, BrandMediaType, LogoVariant, MediaTransformations } from 'types'
import { createBrandMediaFile } from './workspaces/brandVisualKits/useCreateBrandMediaFile'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCreateMediaVariantSync } from './mediaFiles/useCreateMediaVariant'
import { useCreateMediaFile } from './mediaFiles/useCreateMediaFile'
import { useCurrentImageLibrary } from 'hooks/useCurrentImageLibrary'

interface Props {
  brandVisualKitId: number
}

export type Data = BrandMediaFile

export interface Variables {
  brandVisualKitId: number
  variant: LogoVariant.Dark | LogoVariant.Light
  originalLogo: BrandMediaFile
}

export type Options = Omit<UseMutationOptions<Data, unknown, Variables>, 'mutationKey' | 'mutationFn'>

const transformations: Record<LogoVariant.Dark | LogoVariant.Light, MediaTransformations> = {
  [LogoVariant.Light]: {
    grey_scale: {
      brightness: 2,
    },
  },
  [LogoVariant.Dark]: {
    grey_scale: {
      brightness: 0.15,
    },
  },
}

export const useRegenerateLogoVariant = ({ brandVisualKitId }: Props, options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const { mutateAsync: createMediaVariant } = useCreateMediaVariantSync()
  const { mutateAsync: createMediaFile } = useCreateMediaFile()
  const imageLibraryProject = useCurrentImageLibrary()

  if (!imageLibraryProject) {
    throw new Error('Image library project not found')
  }

  return useMutation<Data, unknown, Variables>(
    'regenerateLogoVariant',
    async ({ brandVisualKitId, variant, originalLogo }) => {
      const mediaVariant = await createMediaVariant({
        mediaFileId: originalLogo.media_file.key,
        transformations: transformations[variant],
      })

      const mediaFile = await createMediaFile({
        source_url: mediaVariant.source_url,
        mime_type: mediaVariant.mime_type,
        title: 'logo',
        project_key: imageLibraryProject.key,
        show_in_file_system: false,
      })

      if (!mediaFile.media_file_id) {
        throw new Error('Failed to create media file')
      }

      const brandMediaFile = (
        await createBrandMediaFile({
          brandMediaType: BrandMediaType.LOGO,
          brandVisualKitId,
          mediaFileId: mediaFile.media_file_id,
          logoVariant: variant,
          workspaceId,
          isPrimaryLogo: true,
        })
      ).brand_media_file

      return brandMediaFile
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(['workspaces', workspaceId, 'brand_visual_kits'])
        queryClient.invalidateQueries(['workspaces', workspaceId, 'brand_visual_kits', brandVisualKitId])
        options?.onSuccess?.(data, variables, context)
      },
    },
  )
}
